// components/social/ShareSheet.jsx — choose where to post, edit the text, manage the accounts.
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaShareAlt, FaPlug, FaUnlink, FaKey } from 'react-icons/fa';
import { useSocial } from './SocialProvider';
import { PLATFORMS, defaultCaption } from '../../services/socialService';
import { useConfirm } from '../../contexts/ToastContext';
import './social.css';

export default function ShareSheet({ profile, onClose, mode = 'share' }) {
  const social = useSocial();
  const confirm = useConfirm();
  const [platform, setPlatform] = useState(social.lastPlatform);
  const [caption, setCaption] = useState(() => (profile ? defaultCaption(profile) : ''));
  const [busy, setBusy] = useState(false);
  const image = profile?.cimage || profile?.images?.[0];

  const doShare = async () => {
    setBusy(true);
    try { await social.share(profile, { platform, caption }); onClose(); } finally { setBusy(false); }
  };

  const disconnect = async (p) => {
    const ok = await confirm(`Disconnect ${p.label}? The saved login, cookies and browser profile for it are deleted.`);
    if (ok) await social.disconnect(p.id);
  };

  return createPortal(
    <div className="social-modal-overlay" onClick={onClose}>
      <div className="social-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Share">
        <div className="social-modal-head">
          <strong>{mode === 'accounts' ? 'Social accounts' : 'Share cake'}</strong>
          <button type="button" className="social-btn social-btn--quiet" onClick={onClose} aria-label="Close"><FaTimes /></button>
        </div>

        {!social.worker.online && (
          <div className="social-note social-note--warn">
            The social worker is offline. Tasks are kept and run as soon as it is back (OneAdmin → PCake → social worker).
          </div>
        )}

        <div className="social-platforms">
          {PLATFORMS.map((p) => {
            const acc = social.accounts[p.id];
            const selected = mode === 'share' && platform === p.id;
            return (
              <div key={p.id} className={`social-platform${selected ? ' is-selected' : ''}`}
                onClick={() => mode === 'share' && setPlatform(p.id)} role={mode === 'share' ? 'radio' : undefined} aria-checked={selected}>
                <span className="social-live-badge" style={{ background: p.color }}>{p.short}</span>
                <div className="social-platform-text">
                  <strong>{p.label}{social.lastPlatform === p.id && mode === 'share' ? ' · last used' : ''}</strong>
                  <span>
                    {acc?.connected
                      ? `${acc.accountName || acc.handle || 'Connected'}${acc.needsLogin ? ' — login expired' : ''}`
                      : 'Not connected'}
                    {acc?.hasPassword && <FaKey title={`Saved login ${acc.username || ''}`} style={{ marginLeft: 6, opacity: 0.6 }} />}
                  </span>
                </div>
                <button type="button" className="social-btn" onClick={(e) => { e.stopPropagation(); social.connect(p.id); onClose(); }}
                  title={acc?.connected ? 'Log in again / choose another page' : 'Connect'}>
                  <FaPlug /> <span className="social-hide-sm">{acc?.connected ? 'Reconnect' : 'Connect'}</span>
                </button>
                {acc?.connected && (
                  <button type="button" className="social-btn social-btn--quiet" onClick={(e) => { e.stopPropagation(); disconnect(p); }} title="Disconnect">
                    <FaUnlink />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {mode === 'share' && profile && (
          <>
            <div className="social-share-preview">
              {image && <img src={image} alt="" />}
              <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={7} maxLength={2000} aria-label="Post text" />
            </div>
            <div className="social-modal-foot">
              <button type="button" className="social-btn" onClick={() => setCaption(defaultCaption(profile))}>Reset text</button>
              <button type="button" className="social-btn social-btn--primary" disabled={busy} onClick={doShare}>
                <FaShareAlt /> {social.accounts[platform]?.connected ? 'Share now' : 'Connect & share'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.getElementById('portal-root') || document.body,
  );
}
